import type { Condition } from '../types/conditions';
import { ActionSectionItemAction, SectionBase } from '../types/sections';
import { SectionType } from '../types/SectionType';

export type SectionPropsWithoutSectionBase<S, L> = Omit<S, Exclude<keyof SectionBase, L>>;

export class SectionBaseBuilder {
  private sectionBase: SectionBase;

  constructor() {
    this.sectionBase = {
      sectionName: '',
      systemId: String(Math.random()),
      locale: 'en',
      title: '',
      type: SectionType.Theme,
      automationId: '',
      appDefinitionId: '',
      widgetId: '',
      index: 0,
      groupIds: [],
      panelDesignIds: [],
      panelDesignOverrideIds: [],
      groupOverrideIds: [],
      subNavigationHide: false,
      hide: false,
      showSectionHeader: false,
      noSpaceBeforeSection: false,
      help: {
        hide: false,
        text: '',
        symbolName: '',
        linkText: '',
        link: '',
        image: '',
        video: '',
        openFirstTime: false,
        title: '',
      },
      translations: { title: '', subNavigationTitle: '', presetTitle: '' },
      sectionFooter: {
        label: '',
        hide: false,
        styleOverride: {},
        action: ActionSectionItemAction.OpenMediaManager,
      },
      disableInfoIcon: false,
      conditions: [],
    };
  }

  withSectionName(sectionName: SectionBase['sectionName']): this {
    this.sectionBase.sectionName = sectionName;
    return this;
  }

  withSystemId(systemId: SectionBase['systemId']): this {
    this.sectionBase.systemId = systemId;
    return this;
  }

  withLocale(locale: string): this {
    this.sectionBase.locale = locale;
    return this;
  }

  withTitle(title: SectionBase['title']): this {
    this.sectionBase.title = title;
    return this;
  }

  withType(type: SectionBase['type']): this {
    this.sectionBase.type = type;
    return this;
  }

  withAutomationId(automationId: SectionBase['automationId']): this {
    this.sectionBase.automationId = automationId;
    return this;
  }

  withAppDefinitionId(appDefinitionId: SectionBase['appDefinitionId']): this {
    this.sectionBase.appDefinitionId = appDefinitionId;
    return this;
  }

  withWidgetId(widgetId: SectionBase['widgetId']): this {
    this.sectionBase.widgetId = widgetId;
    return this;
  }

  withIndex(index: SectionBase['index']): this {
    this.sectionBase.index = index;
    return this;
  }

  withGroupIds(groupIds: SectionBase['groupIds']): this {
    this.sectionBase.groupIds = groupIds;
    return this;
  }

  withPanelDesignIds(panelDesignIds: SectionBase['panelDesignIds']): this {
    this.sectionBase.panelDesignIds = panelDesignIds;
    return this;
  }

  withSubNavigationHide(subNavigationHide: SectionBase['subNavigationHide']): this {
    this.sectionBase.subNavigationHide = subNavigationHide;
    return this;
  }

  withHide(hide: SectionBase['hide']): this {
    this.sectionBase.hide = hide;
    return this;
  }

  withShowSectionHeader(showSectionHeader: SectionBase['showSectionHeader']): this {
    this.sectionBase.showSectionHeader = showSectionHeader;
    return this;
  }

  withNoSpaceBeforeSection(noSpaceBeforeSection: SectionBase['noSpaceBeforeSection']): this {
    this.sectionBase.noSpaceBeforeSection = noSpaceBeforeSection;
    return this;
  }

  withHelp(help: SectionBase['help']): this {
    this.sectionBase.help = help;
    return this;
  }

  withTranslations(translations: SectionBase['translations']): this {
    this.sectionBase.translations = translations;
    return this;
  }

  withTranslationTitle(title: string): this {
    this.sectionBase.translations.title = title;
    return this;
  }

  withTranslationPresetTitle(presetTitle: string): this {
    this.sectionBase.translations.presetTitle = presetTitle;
    return this;
  }

  withSectionFooter(sectionFooter: SectionBase['sectionFooter']): this {
    this.sectionBase.sectionFooter = sectionFooter;
    return this;
  }

  withDisableInfoIcon(disableInfoIcon: SectionBase['disableInfoIcon']): this {
    this.sectionBase.disableInfoIcon = disableInfoIcon;
    return this;
  }

  withConditions(...conditions: Condition[]): this {
    this.sectionBase.conditions = conditions;

    return this;
  }

  getTranslations(): SectionBase['translations'] {
    return { ...this.sectionBase.translations };
  }

  build(): SectionBase {
    return { ...this.sectionBase };
  }
}
